import React, { ComponentProps, Ref } from "react";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import { BasquiatPaddingProps, useUnitPropsConverter } from "./units";
import Gap from "./Gap";

type HStackAlignment = "top" | "center" | "bottom" | "stretch";
type HStackFit = "fill" | "center" | "none" | "flex";

export type HStackProps = {
    alignment?: HStackAlignment;
    fit?: HStackFit;
    gap?: number | string;
    style?: StyleProp<ViewStyle>;
    children: React.ReactNode;
    onLayout?: ComponentProps<typeof View>["onLayout"];
    wrapChildren?: boolean; // If true, do not wrap children with additional View (useful for performance optimization)
} & BasquiatPaddingProps;

const ALIGNMENT_MAP: { [key: string]: StyleProp<ViewStyle> } = {
    top: { alignItems: "flex-start" },
    center: { alignItems: "center" },
    bottom: { alignItems: "flex-end" },
    stretch: { alignItems: "stretch" },
};

const FIT_MAP: { [key: string]: StyleProp<ViewStyle> } = {
    center: { alignContent: "center", justifyContent: "center" },
    fill: { justifyContent: "space-between" },
    none: {},
    flex: {},
};

const CHILD_FIT_MAP: { [key: string]: StyleProp<ViewStyle> } = {
    center: {},
    fill: { flex: 1 },
    none: {},
    flex: { flex: 1 },
};

const styles = StyleSheet.create({
    hStack: {
        flexDirection: "row",
    },
});

export const HStack = ({
    gap,
    alignment = "center",
    fit = "none",
    style,
    children,
    onLayout,
    wrapChildren = true,
    ref,
    ...paddingProps // BasquiatPaddingProps
}: HStackProps & { ref?: Ref<View> }) => {
    const childrenCount = React.Children.count(children);
    const propsConverter = useUnitPropsConverter();
    const paddingStyle = propsConverter(paddingProps);
    return (
        <View
            ref={ref}
            onLayout={onLayout}
            style={[styles.hStack, paddingStyle, ALIGNMENT_MAP[alignment], FIT_MAP[fit], style]}
        >
            {gap && gap !== 0 && childrenCount > 1
                ? React.Children.map(children, (child, index) => (
                      <>
                          {wrapChildren && <View style={CHILD_FIT_MAP[fit]}>{child}</View>}
                          {!wrapChildren && child}
                          {index < childrenCount - 1 ? <Gap gap={gap} mode="horizontal" /> : null}
                      </>
                  ))
                : children}
        </View>
    );
};

export default HStack;
